import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import { CheckCircleOutline, Close, ContentCopy } from '@mui/icons-material';
import Handlebars from 'handlebars';

import { getMailById, getEmailTemplateById, getMailDeliveryStatus, markMailManuallyResolved } from '../../services/MailService';
import { getAuthUserId } from '../../services/AuthService';
import { getMailDeliveryStatusDisplay } from '../../utils/MailUtils';

const registerHandlebarsHelpers = () => {
  if (!Handlebars.helpers.and) {
    Handlebars.registerHelper('and', (...args) => {
      const options = args[args.length - 1];
      const values = args.slice(0, -1);
      const result = values.every(Boolean);
      if (options && typeof options.fn === 'function') {
        return result ? options.fn(this) : options.inverse(this);
      }
      return result;
    });
  }
  if (!Handlebars.helpers.or) {
    Handlebars.registerHelper('or', (...args) => {
      const options = args[args.length - 1];
      const values = args.slice(0, -1);
      const result = values.some(Boolean);
      if (options && typeof options.fn === 'function') {
        return result ? options.fn(this) : options.inverse(this);
      }
      return result;
    });
  }
  if (!Handlebars.helpers.not) {
    Handlebars.registerHelper('not', (value, options) => {
      const result = !value;
      if (options && typeof options.fn === 'function') {
        return result ? options.fn(this) : options.inverse(this);
      }
      return result;
    });
  }
};

const MailDetailsDialog = ({ open, mailId, patientEmail, onClose, canResolve = false, onResolved }) => {
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState(null);

  const [renderedSubject, setRenderedSubject] = useState('');
  const [renderedHtml, setRenderedHtml] = useState('');
  const [renderedText, setRenderedText] = useState('');
  const [deliveryStatus, setDeliveryStatus] = useState(null);
  const [resolutionStatus, setResolutionStatus] = useState(null);

  const [confirmLoading, setConfirmLoading] = useState(false);

  const contentRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!open || !mailId) return;
      setDetailsLoading(true);
      setDetailsError(null);
      setRenderedSubject('');
      setRenderedHtml('');
      setRenderedText('');
      setDeliveryStatus(null);
      setResolutionStatus(null);

      try {
        const mail = await getMailById(mailId);
        const templateId = mail?.sendGrid?.templateId;
        const dynamicTemplateData = mail?.sendGrid?.dynamicTemplateData ?? {};
        const template = await getEmailTemplateById(templateId);

        const htmlContent = template?.htmlContent ?? template?.html ?? '';
        const subject = template?.subject ?? null;

        if (!htmlContent) {
          throw new Error("No se encontró el contenido de la plantilla para renderizar.");
        }

        registerHandlebarsHelpers();
        const templateFn = Handlebars.compile(htmlContent);
        const outputHtml = templateFn(dynamicTemplateData);

        let outputSubject = mail?.message?.subject ?? '';
        if (subject) {
          outputSubject = Handlebars.compile(subject)(dynamicTemplateData);
        }

        // Force links to open in a new tab and prevent horizontal scroll from long tokens.
        const dom = new DOMParser().parseFromString(outputHtml, 'text/html');
        dom.querySelectorAll('a[href]').forEach((a) => {
          a.setAttribute('target', '_blank');
          a.setAttribute('rel', 'noopener noreferrer');
        });

        const updatedHtml = dom.body.innerHTML;
        const updatedText = dom.body.innerText;

        if (cancelled) return;
        setRenderedSubject(outputSubject);
        setRenderedHtml(updatedHtml);
        setRenderedText(updatedText);

        setDeliveryStatus(getMailDeliveryStatus(mail?.delivery));
        setResolutionStatus(mail?.delivery?.resolution?.status ?? null);
      } catch (err) {
        if (cancelled) return;
        setDetailsError(err?.message ?? 'Error al cargar el correo.');
      } finally {
        if (!cancelled) setDetailsLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [open, mailId]);

  const shouldShowManualResolve = (statusValue, mailResolutionStatus) =>
    canResolve
    && mailResolutionStatus !== 'manually_resolved'
    && getMailDeliveryStatusDisplay(statusValue).allowsManualResolve;

  const copyRenderedContent = async () => {
    try {
      // Best-effort: copy exactly like user selecting rendered content.
      if (contentRef.current) {
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(contentRef.current);

        selection.removeAllRanges();
        selection.addRange(range);
        document.execCommand('copy');
        selection.removeAllRanges();
        return;
      }

      if (!renderedText) return;
      await navigator.clipboard.writeText(renderedText);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = renderedText ?? '';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
  };

  const copySubject = async () => {
    const subject = renderedSubject ?? '';
    if (!subject) return;
    try {
      await navigator.clipboard.writeText(subject);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = subject;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
  };

  const copyRecipientEmail = async () => {
    const recipientEmail = patientEmail ?? '';
    if (!recipientEmail) return;
    try {
      await navigator.clipboard.writeText(recipientEmail);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = recipientEmail;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
  };

  const confirmManualResolution = async () => {
    if (!mailId) return;
    setConfirmLoading(true);
    try {
      const resolvedBy = getAuthUserId?.() ?? null;
      await markMailManuallyResolved(mailId, resolvedBy);
      if (onResolved) await onResolved();
      onClose?.();
    } catch (err) {
      setDetailsError(err?.message ?? 'Error al confirmar la resolución manual.');
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <Dialog open={open} maxWidth="md" fullWidth onClose={() => onClose?.()}>
      <DialogTitle sx={{ position: 'relative', pr: 7, pt: 2.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, width: '100%', minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{
                maxWidth: 'calc(100% - 48px)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {patientEmail ? `Correo: ${patientEmail}` : 'Detalle de correo'}
            </Typography>
            <Tooltip title="Copiar correo">
              <IconButton
                size="small"
                aria-label="copy-recipient-email"
                onClick={copyRecipientEmail}
                disabled={!patientEmail || detailsLoading}
                sx={{ flexShrink: 0 }}
              >
                <ContentCopy fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{
                maxWidth: 'calc(100% - 48px)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {renderedSubject ? `Asunto: ${renderedSubject}` : 'Detalle de correo'}
            </Typography>
            <Tooltip title="Copiar asunto">
              <IconButton
                size="small"
                aria-label="copy-subject"
                onClick={copySubject}
                disabled={!renderedSubject || detailsLoading}
                sx={{ flexShrink: 0 }}
              >
                <ContentCopy fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <IconButton
          aria-label="close"
          onClick={() => onClose?.()}
          sx={{ position: 'absolute', top: 8, right: 8 }}
          size="small"
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ overflowX: 'hidden', px: 3, pb: 1 }}>
        {detailsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : detailsError ? (
          <Typography color="error">{detailsError}</Typography>
        ) : (
          <>
            <Box
              ref={contentRef}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 1,
                maxHeight: '60vh',
                overflow: 'auto',
                overflowX: 'hidden',
                '& a': { overflowWrap: 'anywhere', wordBreak: 'break-word' },
                '& *': { overflowWrap: 'anywhere' },
                '& a:link, & a:visited': {
                  color: 'primary.main',
                  textDecoration: 'underline'
                },
                '& img': { maxWidth: '100%' }
              }}
              dangerouslySetInnerHTML={{ __html: renderedHtml }}
            />
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pt: 1, pb: 3 }}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ width: '100%', justifyContent: 'space-between' }}
        >
          <Button
            variant="outlined"
            startIcon={<ContentCopy />}
            onClick={copyRenderedContent}
            disabled={!renderedText || detailsLoading}
          >
            Copiar
          </Button>
          {shouldShowManualResolve(deliveryStatus, resolutionStatus) && (
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleOutline />}
              onClick={confirmManualResolution}
              disabled={confirmLoading || detailsLoading}
            >
              Confirmar resolución
            </Button>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default MailDetailsDialog;

